/**
 * Router minimo sobre la History API.
 *
 * La aplicacion tiene un conjunto fijo y pequeño de pantallas, sin rutas
 * anidadas ni carga de datos por ruta. Una libreria de enrutamiento traeria
 * mas superficie de la que este caso necesita; esto son sesenta lineas que se
 * leen completas y no cambian.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface RouterValue {
  path: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
  back: () => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }): ReactNode {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (to === window.location.pathname) return;
    if (options?.replace) window.history.replaceState(null, '', to);
    else window.history.pushState(null, '', to);
    setPath(to);
    // Cambiar de pantalla siempre arranca desde arriba; conservar el scroll de
    // la pantalla anterior desorienta.
    window.scrollTo(0, 0);
  }, []);

  const back = useCallback(() => window.history.back(), []);

  const value = useMemo<RouterValue>(() => ({ path, navigate, back }), [path, navigate, back]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter debe usarse dentro de RouterProvider');
  return value;
}

/**
 * Enlace de navegacion.
 *
 * Respeta el clic con modificadores y el clic con el boton central: quien
 * quiere abrir en otra pestaña debe poder hacerlo, incluso en una PWA.
 */
export function Link({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}): ReactNode {
  const { navigate } = useRouter();

  return (
    <a
      href={to}
      className={className}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button !== 0) return;
        event.preventDefault();
        onClick?.();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/** True si `path` corresponde a la ruta actual o a una subruta suya. */
export function isActivePath(current: string, target: string): boolean {
  if (target === '/') return current === '/';
  return current === target || current.startsWith(`${target}/`);
}
